import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Notification } from '@/hooks/useNotifications';
import {
  InboxCategory,
  WorkspaceMessageItem,
  AccessRequestItem,
} from '@/hooks/useUnifiedInbox';
import { formatDistanceToNow } from 'date-fns';
import { de } from 'date-fns/locale';
import {
  Check,
  CheckCheck,
  Loader2,
  Users,
  TrendingUp,
  Settings,
  UserPlus,
  FileCheck,
  MessageSquare,
  ShieldAlert,
  AtSign,
  Inbox,
  Mail,
  Reply,
} from 'lucide-react';

interface UnifiedInboxProps {
  notifications: Notification[];
  categorizedNotifications: {
    all: Notification[];
    messages: Notification[];
    mentions: Notification[];
    requests: Notification[];
  };
  messages: WorkspaceMessageItem[];
  accessRequests: AccessRequestItem[];
  loading: boolean;
  unreadMessages: number;
  pendingRequests: number;
  unreadMentions: number;
  totalUnread: number;
  onMarkAsRead: (id: string) => void;
  onMarkAllAsRead: () => void;
  onClose: () => void;
  onOpenTeamMessages?: (recipientId?: string) => void;
  onOpenAccessRequests?: () => void;
}

const getNotificationIcon = (type: string) => {
  switch (type) {
    case 'new_decision':
      return <FileCheck className="h-4 w-4 text-primary" />;
    case 'phase_change':
      return <TrendingUp className="h-4 w-4 text-primary" />;
    case 'member_joined':
      return <UserPlus className="h-4 w-4 text-primary" />;
    case 'team_update':
      return <Users className="h-4 w-4 text-primary" />;
    case 'comment':
    case 'mention':
      return <AtSign className="h-4 w-4 text-primary" />;
    case 'workspace_message':
      return <MessageSquare className="h-4 w-4 text-primary" />;
    case 'access_request':
      return <ShieldAlert className="h-4 w-4 text-primary" />;
    default:
      return <Settings className="h-4 w-4 text-muted-foreground" />;
  }
};

const formatTime = (date: string) => {
  try {
    return formatDistanceToNow(new Date(date), { locale: de, addSuffix: true });
  } catch {
    return 'Kürzlich';
  }
};

export const UnifiedInbox = ({
  notifications,
  categorizedNotifications,
  messages,
  accessRequests,
  loading,
  unreadMessages,
  pendingRequests,
  unreadMentions,
  totalUnread,
  onMarkAsRead,
  onMarkAllAsRead,
  onClose,
  onOpenTeamMessages,
  onOpenAccessRequests,
}: UnifiedInboxProps) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<InboxCategory>('all');

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.is_read) {
      onMarkAsRead(notification.id);
    }

    if (notification.type === 'workspace_message') {
      onClose();
      const senderId = notification.data?.sender_id as string | undefined;
      onOpenTeamMessages?.(senderId);
      return;
    }
    if (notification.type === 'access_request') {
      onClose();
      onOpenAccessRequests?.();
      return;
    }

    if (notification.data?.session_id) {
      navigate(`/app/session/${notification.data.session_id}`);
      onClose();
    }
  };

  const handleMessageClick = (msg: WorkspaceMessageItem) => {
    onClose();
    onOpenTeamMessages?.(msg.sender_id);
  };

  const handleRequestClick = () => {
    onClose();
    onOpenAccessRequests?.();
  };

  const renderNotification = (notification: Notification) => (
    <button
      key={notification.id}
      onClick={() => handleNotificationClick(notification)}
      className={`w-full p-3 text-left hover:bg-accent/50 transition-colors ${
        !notification.is_read ? 'bg-accent/20' : ''
      }`}
    >
      <div className="flex items-start gap-3">
        <div className="mt-0.5">{getNotificationIcon(notification.type)}</div>
        <div className="flex-1 min-w-0 space-y-0.5">
          <div className="flex items-center gap-2">
            <p className="font-semibold text-sm text-foreground truncate">
              {notification.title}
            </p>
            {!notification.is_read && (
              <span className="h-2 w-2 rounded-full bg-primary flex-shrink-0" />
            )}
          </div>
          <p className="text-xs text-muted-foreground line-clamp-2">
            {notification.message}
          </p>
          <p className="text-[10px] text-muted-foreground">
            {formatTime(notification.created_at)}
          </p>
        </div>
        {!notification.is_read && (
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onMarkAsRead(notification.id);
            }}
            className="h-7 w-7 p-0 flex-shrink-0"
          >
            <Check className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>
    </button>
  );

  const renderMessageItem = (msg: WorkspaceMessageItem) => (
    <button
      key={msg.id}
      onClick={() => handleMessageClick(msg)}
      className={`w-full p-3 text-left hover:bg-accent/50 transition-colors ${
        !msg.is_read ? 'bg-accent/20' : ''
      }`}
    >
      <div className="flex items-start gap-3">
        <div className="mt-0.5">
          <Mail className="h-4 w-4 text-primary" />
        </div>
        <div className="flex-1 min-w-0 space-y-0.5">
          <div className="flex items-center gap-2">
            <p className="font-semibold text-sm text-foreground truncate">
              {msg.sender_name || msg.sender_email || 'Unbekannt'}
            </p>
            {!msg.is_read && (
              <span className="h-2 w-2 rounded-full bg-primary flex-shrink-0" />
            )}
          </div>
          <p className="text-xs text-muted-foreground line-clamp-2">
            {msg.content}
          </p>
          <div className="flex items-center gap-2">
            <p className="text-[10px] text-muted-foreground">
              {formatTime(msg.created_at)}
            </p>
            <Badge variant="outline" className="text-[9px] h-3.5 px-1">
              {msg.recipient_id === null ? 'An Admins' : 'Direkt'}
            </Badge>
          </div>
        </div>
        <Reply className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0 mt-1" />
      </div>
    </button>
  );

  const renderAccessRequest = (req: AccessRequestItem) => (
    <button
      key={req.id}
      onClick={handleRequestClick}
      className="w-full p-3 text-left hover:bg-accent/50 transition-colors bg-accent/10"
    >
      <div className="flex items-start gap-3">
        <div className="mt-0.5">
          <UserPlus className="h-4 w-4 text-primary" />
        </div>
        <div className="flex-1 min-w-0 space-y-0.5">
          <div className="flex items-center gap-2">
            <p className="font-semibold text-sm text-foreground truncate">
              {req.requester_name || req.requester_email || 'Unbekannt'}
            </p>
            <span className="h-2 w-2 rounded-full bg-primary flex-shrink-0" />
          </div>
          <p className="text-xs text-muted-foreground">
            Möchte dem Workspace beitreten
          </p>
          {req.message && (
            <p className="text-xs text-muted-foreground line-clamp-1 italic">
              „{req.message}"
            </p>
          )}
          <p className="text-[10px] text-muted-foreground">
            {formatTime(req.created_at)}
          </p>
        </div>
        <Badge variant="secondary" className="text-[10px] flex-shrink-0">
          Ausstehend
        </Badge>
      </div>
    </button>
  );

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center gap-2">
          <Inbox className="h-4 w-4 text-primary" />
          <h3 className="font-semibold text-foreground">Inbox</h3>
          {totalUnread > 0 && (
            <Badge variant="secondary" className="text-xs h-5 px-1.5">
              {totalUnread}
            </Badge>
          )}
        </div>
        {totalUnread > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onMarkAllAsRead}
            className="text-xs h-7"
          >
            <CheckCheck className="h-3 w-3 mr-1" />
            Alle gelesen
          </Button>
        )}
      </div>

      {/* Category Tabs */}
      <Tabs
        value={activeTab}
        onValueChange={(v) => setActiveTab(v as InboxCategory)}
        className="w-full"
      >
        <div className="px-2 pt-2">
          <TabsList className="grid w-full grid-cols-4 h-8">
            <TabsTrigger value="all" className="text-xs gap-1">
              Alle
            </TabsTrigger>
            <TabsTrigger value="messages" className="text-xs gap-1 relative">
              <MessageSquare className="h-3 w-3" />
              {unreadMessages > 0 && (
                <span className="absolute -top-0.5 -right-0.5 h-3.5 w-3.5 rounded-full bg-primary text-primary-foreground text-[9px] flex items-center justify-center">
                  {unreadMessages}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="requests" className="text-xs gap-1 relative">
              <ShieldAlert className="h-3 w-3" />
              {pendingRequests > 0 && (
                <span className="absolute -top-0.5 -right-0.5 h-3.5 w-3.5 rounded-full bg-primary text-primary-foreground text-[9px] flex items-center justify-center">
                  {pendingRequests}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="mentions" className="text-xs gap-1 relative">
              <AtSign className="h-3 w-3" />
              {unreadMentions > 0 && (
                <span className="absolute -top-0.5 -right-0.5 h-3.5 w-3.5 rounded-full bg-primary text-primary-foreground text-[9px] flex items-center justify-center">
                  {unreadMentions}
                </span>
              )}
            </TabsTrigger>
          </TabsList>
        </div>

        {/* All notifications */}
        <TabsContent value="all" className="mt-0">
          {categorizedNotifications.all.length === 0 ? (
            <EmptyState label="Keine Benachrichtigungen" />
          ) : (
            <ScrollArea className="h-[360px]">
              <div className="divide-y divide-border">
                {categorizedNotifications.all.map(renderNotification)}
              </div>
            </ScrollArea>
          )}
        </TabsContent>

        {/* Messages tab - shows direct workspace messages */}
        <TabsContent value="messages" className="mt-0">
          {messages.length === 0 ? (
            <EmptyState label="Keine Nachrichten" />
          ) : (
            <ScrollArea className="h-[360px]">
              <div className="divide-y divide-border">
                {messages.map(renderMessageItem)}
              </div>
            </ScrollArea>
          )}
        </TabsContent>

        {/* Access Requests tab */}
        <TabsContent value="requests" className="mt-0">
          {accessRequests.length === 0 ? (
            <EmptyState label="Keine offenen Anfragen" />
          ) : (
            <ScrollArea className="h-[360px]">
              <div className="divide-y divide-border">
                {accessRequests.map(renderAccessRequest)}
              </div>
            </ScrollArea>
          )}
        </TabsContent>

        {/* Mentions tab */}
        <TabsContent value="mentions" className="mt-0">
          {categorizedNotifications.mentions.length === 0 ? (
            <EmptyState label="Keine Erwähnungen" />
          ) : (
            <ScrollArea className="h-[360px]">
              <div className="divide-y divide-border">
                {categorizedNotifications.mentions.map(renderNotification)}
              </div>
            </ScrollArea>
          )}
        </TabsContent>
      </Tabs>

      {/* Footer */}
      <div className="p-2 border-t border-border">
        <Button
          variant="ghost"
          size="sm"
          className="w-full justify-center text-xs h-8"
          onClick={() => {
            onClose();
            setTimeout(() => navigate('/settings/notifications'), 100);
          }}
        >
          <Settings className="h-3 w-3 mr-1.5" />
          Einstellungen
        </Button>
      </div>
    </div>
  );
};

function EmptyState({ label }: { label: string }) {
  return (
    <div className="py-12 text-center">
      <Inbox className="h-8 w-8 mx-auto text-muted-foreground/30 mb-2" />
      <p className="text-sm text-muted-foreground">{label}</p>
    </div>
  );
}
